import {
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import { TEMPLATES, type TemplateMeta } from "../../shared/templates";
import { endpoints, type ResumeSummary } from "../api/client";
import { resumesQuery, useNotifyError } from "../api/queries";
import { useAuth } from "../auth/provider";
import { AppFrame } from "../components/app-frame";
import { Page, PageHeader } from "../components/page";
import { TemplateThumb } from "../components/template-thumb";
import { sampleDocument, withTemplate } from "../cv/sample";
import { useLocale, useMessages } from "../i18n/provider";
import styles from "./templates.module.css";

type Filter = "all" | "free" | "pro";

export function TemplatesPage() {
  const t = useMessages();
  const { locale } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const { data } = useQuery(resumesQuery);
  const resumes = data?.items ?? [];

  const [filter, setFilter] = useState<Filter>("all");
  const [active, setActive] = useState<TemplateMeta | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const samples = useMemo(() => {
    const map = new Map<string, ReturnType<typeof sampleDocument>>();
    for (const template of TEMPLATES) {
      map.set(
        template.id,
        withTemplate(
          sampleDocument(locale),
          template.id,
          template.accent,
          template.fontPair,
          template.density,
        ),
      );
    }
    return map;
  }, [locale]);

  const visible = TEMPLATES.filter((template) => filter === "all" || template.tier === filter);

  const openTemplate = (template: TemplateMeta) => {
    setActive(template);
    setResumeId(resumes[0]?.id ?? null);
  };

  const applyToResume = async () => {
    if (!active || !resumeId) return;
    setBusy(true);
    try {
      await endpoints.updateResume(resumeId, { templateId: active.id });
      await queryClient.invalidateQueries({ queryKey: ["resumes"] });
      await queryClient.invalidateQueries({ queryKey: ["resume", resumeId] });
      notifications.show({
        color: "gray",
        message: t("editor.templateChanged"),
        autoClose: 2500,
      });
      setActive(null);
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  const createWithTemplate = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const created = await endpoints.createResume({
        title: t(active.nameKey as MessageKey),
        templateId: active.id,
        locale,
      });
      await queryClient.invalidateQueries({ queryKey: ["resumes"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      setActive(null);
      navigate(`/resumes/${created.id}/edit`);
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  const locked = active?.tier === "pro" && !me?.limits.allTemplates;

  return (
    <AppFrame active="/templates">
      <Page>
        <PageHeader
          title={t("templates.title")}
          description={t("landing.proofTemplates")}
          action={
            <SegmentedControl
              size="xs"
              value={filter}
              onChange={(value) => setFilter(value as Filter)}
              data={[
                { value: "all", label: t("templates.all") },
                { value: "free", label: t("common.free") },
                { value: "pro", label: t("common.pro") },
              ]}
            />
          }
        />

        <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 4 }} spacing="lg">
          {visible.map((template) => {
            const doc = samples.get(template.id);
            if (!doc) return null;
            return (
              <article key={template.id} className={styles.card}>
                <button
                  type="button"
                  className={styles.frame}
                  onClick={() => openTemplate(template)}
                  aria-label={t(template.nameKey as MessageKey)}
                >
                  <TemplateThumb doc={doc} />
                </button>
                <div className={styles.body}>
                  <Group justify="space-between" gap={8} wrap="nowrap">
                    <span className={styles.name}>{t(template.nameKey as MessageKey)}</span>
                    <Badge
                      size="xs"
                      variant="light"
                      color={template.tier === "pro" ? "blue" : "gray"}
                    >
                      {template.tier === "pro" ? t("common.pro") : t("common.free")}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed" mt={4}>
                    {t(template.descriptionKey as MessageKey)}
                  </Text>
                  <Button
                    variant="light"
                    size="compact-sm"
                    mt="sm"
                    fullWidth
                    onClick={() => openTemplate(template)}
                  >
                    {t("templates.use")}
                  </Button>
                </div>
              </article>
            );
          })}
        </SimpleGrid>
      </Page>

      <Modal
        opened={Boolean(active)}
        onClose={() => setActive(null)}
        title={
          active ? (locked ? t("templates.proRequired") : t(active.nameKey as MessageKey)) : ""
        }
        size="sm"
      >
        {locked ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t("templates.upgradeBody")}
            </Text>
            <Button onClick={() => navigate("/billing")}>{t("templates.upgradeCta")}</Button>
          </Stack>
        ) : (
          <Stack gap="md">
            {resumes.length > 0 ? (
              <Select
                label={t("templates.chooseCv")}
                data={resumes.map((resume: ResumeSummary) => ({
                  value: resume.id,
                  label: resume.title,
                }))}
                value={resumeId}
                onChange={setResumeId}
                allowDeselect={false}
              />
            ) : null}
            <Stack gap={8}>
              {resumes.length > 0 ? (
                <Button onClick={() => void applyToResume()} loading={busy} disabled={!resumeId}>
                  {t("templates.apply")}
                </Button>
              ) : null}
              <Button
                variant={resumes.length > 0 ? "light" : "filled"}
                onClick={() => void createWithTemplate()}
                loading={busy}
              >
                {t("dashboard.newCv")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Modal>
    </AppFrame>
  );
}
